import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Badge, Modal } from 'react-bootstrap';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { PencilSquare, Trash } from 'react-bootstrap-icons';


import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

const exampleStudies = [
  {
    name: 'Meyskens 1994',
    pdfPath: '/example/Meyskens-et-al-1994.pdf',
    mdPath: '/example/Meyskens-et-al-1994.md',
  },
  {
    name: 'Ruffin 2004',
    pdfPath: '/example/Ruffin-et-al-2004.pdf',
    mdPath: '/example/Ruffin-et-al-2004.md',
  },
];


export default function StudyUploader(props) {
  const [studyName, setStudyName] = useState('');
  const [studyFiles, setStudyFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [pdfModal, setPdfModal] = useState({ show: false, fileUrl: null, name: '' });
  const [numPages, setNumPages] = useState(null);
  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(null);

  const [viewMarkdown, setViewMarkdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {studies, setStudies} = props;


  useEffect(() => {
    if (pdfModal.show && pdfContainerRef.current) {
      setPdfWidth(pdfContainerRef.current.offsetWidth);
    }
  }, [pdfModal.show]);

  const handleOpenModal = () => {
    setStudyName('');
    setStudyFiles([]);
    setEditId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setStudyFiles(selected);
  };

  const handleSaveStudy = async () => {
    if (!studyName || studyFiles.length === 0) return;

    setIsSaving(true); // start spinner

    const convertedFiles = [];

    for (const file of studyFiles) {
      // If file is already a { file, markdown } object, skip conversion
      if (file.markdown !== undefined) {
        convertedFiles.push(file);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/convert_pdf`, {
          method: 'POST',
          body: formData,
        });

        const data = await res.json();
        convertedFiles.push({
          file,
          markdown: data.markdown ?? `Error: ${data.error ?? 'Unknown error'}`,
        });
      } catch (err) {
        convertedFiles.push({
          file,
          markdown: `Error: ${err.message}`,
        });
      }
    }

    const newStudy = {
      id: editId ?? Date.now(),
      name: studyName,
      files: convertedFiles,
    };

    if (editId !== null) {
      setStudies(studies.map((s) => (s.id === editId ? newStudy : s)));
    } else {
      setStudies([...studies, newStudy]);
    }

    setIsSaving(false); // stop spinner
    setShowModal(false);
  };


  const handleEditStudy = (id) => {
    const study = studies.find((s) => s.id === id);
    setStudyName(study.name);
    setStudyFiles(study.files);
    setEditId(id);
    setShowModal(true);
  };

  const handleDeleteStudy = (id) => {
    setStudies(studies.filter((s) => s.id !== id));
  };


  const handleOpenPdf = (fileObj) => {
    const fileUrl = URL.createObjectURL(fileObj.file);
    setPdfModal({ show: true, fileUrl, name: fileObj.file.name, markdown: fileObj.markdown });
  };

  const handleClosePdf = () => {
    if (pdfModal.fileUrl) {
      URL.revokeObjectURL(pdfModal.fileUrl);
    }
    setPdfModal({ show: false, fileUrl: null, name: '' });
    setNumPages(null);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // load example
  const handleLoadExample = async () => {
    const loadedStudies = await Promise.all(
      exampleStudies.map(async ({ name, pdfPath, mdPath }, idx) => {
        try {
          const [pdfRes, mdRes] = await Promise.all([
            fetch(pdfPath),
            fetch(mdPath),
          ]);

          const pdfBlob = await pdfRes.blob();
          const pdfFile = new File([pdfBlob], pdfPath.split('/').pop(), { type: 'application/pdf' });
          const markdown = await mdRes.text();

          return {
            id: idx,
            name,
            files: [{ file: pdfFile, markdown }],
          };
        } catch (err) {
          console.error(`Error loading ${name}:`, err);
          return null;
        }
      })
    );

    const validStudies = loadedStudies.filter(Boolean);
    setStudies([...studies, ...validStudies]);
  };

  return (
      <>
        <Card className="p-4 shadow-sm rounded-4 mb-4">
          <h4 className="mb-3">Your Studies</h4>
          {studies.length === 0 ? (
              <p className="text-muted text-center">No studies added yet. Click "Add Study" to begin.</p>
          ) : (
              studies.map((study) => (
                  <Card key={study.id} className="mb-3 p-3 bg-light rounded-3">
                    <Row className="align-items-center">
                      <Col xs={12} md={3} className="fw-semibold">
                        {study.name}
                      </Col>
                      <Col xs={12} md={5} className="my-2 my-md-0">
                        {study.files.map((fileObj, idx) => (
                            <Badge
                                bg="secondary"
                                key={idx}
                                onClick={() => handleOpenPdf(fileObj)}
                                className="me-2 mb-1 text-truncate"
                                style={{
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  cursor: 'pointer',
                                  display: 'inline-block',
                                  verticalAlign: 'middle',
                                }}
                            >
                              {fileObj.file.name}
                            </Badge>
                        ))}
                      </Col>
                      <Col xs={12} md={4} className="text-md-end">
                        <Button
                            size="sm"
                            className="p-1 me-2 d-inline-flex align-items-center justify-content-center logo-color-outline"
                            onClick={() => handleEditStudy(study.id)}
                            title="Edit"
                        >
                          <PencilSquare size={16}/>
                        </Button>

                        <Button variant="outline-danger" size="sm"
                                className="p-1 me-2 d-inline-flex align-items-center justify-content-center"
                                onClick={() => handleDeleteStudy(study.id)} title="Delete">
                          <Trash/>
                        </Button>
                      </Col>
                    </Row>
                  </Card>
              ))
          )}
          <div className="text-center mt-3">
            <Button className="logo-color" onClick={handleOpenModal}>Add Study</Button>
          </div>

          {studies.length === 0 && (
            <div className="text-center mt-3">
              <p className="text-muted mb-0">
                Don’t have a study at hand?{' '}
                <span
                    onClick={handleLoadExample}
                    className="text-decoration-underline logo-color-txt"
                    role="button"
                    style={{cursor: 'pointer'}}
                >
                  Load example
                </span>
              </p>
            </div>
          )}
        </Card>

        {/* Study Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton className="logo-color text-white">
            <Modal.Title>{editId !== null ? 'Edit Study' : 'Add Study'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
              <Form.Label>Study Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter study name"
                value={studyName}
                onChange={(e) => setStudyName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveStudy();
                  }
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Upload PDFs</Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf"
                multiple
                onChange={handleFileChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
          <Button className="logo-color" onClick={handleSaveStudy} disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Converting...
              </>
            ) : (
              'Save Study'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* PDF Viewer Modal */}
      <Modal show={pdfModal.show} onHide={handleClosePdf} size="xl" centered scrollable>
        <Modal.Header closeButton className="logo-color text-white">
          <Modal.Title>{pdfModal.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{height: '80vh'}}>
          <div className="mb-3">
            <Form.Check
                type="switch"
                id="toggle-markdown"
                label={viewMarkdown ?  "View PDF": "View Markdown"}
                checked={viewMarkdown}
                onChange={(e) => setViewMarkdown(e.target.checked)}
            />
          </div>

          <div ref={pdfContainerRef} style={{overflowY: 'auto', maxHeight: '70vh'}}>
            {viewMarkdown ? (
                <pre style={{whiteSpace: 'pre-wrap'}}>{pdfModal.markdown}</pre>
            ) : (
                pdfModal.fileUrl && (
                    <Document file={pdfModal.fileUrl} onLoadSuccess={onDocumentLoadSuccess}>
                      {Array.from(new Array(numPages), (_, i) => (
                          <Page key={`page_${i + 1}`} pageNumber={i + 1} width={pdfWidth}/>
                      ))}
                    </Document>
                )
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="logo-color" onClick={handleClosePdf}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
